/**
 * Info economy state (Sprint 2a): coin-targeted news feed, leaks (rumors),
 * and hidden whale cycles. Pure data only (no window/document/PIXI).
 */
import { COINS } from './config/coins.js';
import { NEWS_CONFIG, type NewsDirection } from './config/coinNews.js';
import { WHALE_CONFIG } from './config/whale.js';
import { migrateBags, type BagStore } from './shuffleBag.js';
import type { SeededComment } from './config/comments.js';

/** A news effect currently pushing a coin's momentum */
export interface ActiveCoinNews {
  instanceId: string;
  newsId: string;
  coinId: string;
  direction: NewsDirection;
  impact: number;
  publishedAt: number;
  expiresAt: number;
  /**
   * Optional embargo: the price move does not start until this time. Used to
   * give fact-checkers a front-running window (rumor confirmed early, price
   * moves later). Absent on legacy / plain-news entries = starts immediately.
   */
  startsAt?: number;
}

/** Published news entry in the feed */
export interface FeedNewsItem {
  kind: 'news';
  instanceId: string;
  newsId: string;
  coinId: string;
  direction: NewsDirection;
  publishedAt: number;
  /**
   * Seeded NPC comments drawn from the per-direction shuffle bag at publish time
   * (so memes don't repeat across feed items until the pool is exhausted).
   * Absent on legacy saves -> the view falls back to deterministic seedComments.
   */
  seeded?: SeededComment[];
}

/** Leak (찌라시) entry in the feed. Truth lives in pendingNews until reveal. */
export interface LeakItem {
  kind: 'leak';
  leakId: string;
  /** phrasing variant 1..NEWS_CONFIG.leakVariants */
  variant: number;
  claimCoinId: string;
  claimDirection: NewsDirection;
  genuine: boolean;
  createdAt: number;
  revealAt: number;
  factChecked: boolean;
  /** set true when the real news publishes (verdict visible in feed) */
  resolved: boolean;
}

export type FeedItem = FeedNewsItem | LeakItem;

/** Player-authored comment on a news item (Sprint 3a) */
export interface PlayerComment {
  instanceId: string;
  /** free text, or empty when the comment is a stance pick */
  text: string;
  /** index into STANCE_CHOICES when this was a stance tap (else undefined) */
  stanceIdx?: number;
  at: number;
}

/** NPC reply reply-pool mood; undefined = legacy generic npc_reply_N pool */
export type ReplyMood = 'bull' | 'bear' | 'neut';

/** Delivered NPC reaction to a player comment (indices into static i18n pools) */
export interface NpcReply {
  instanceId: string;
  nickIdx: number;
  replyIdx: number;
  /** when set, reply text = npc_reply_{mood}_{replyIdx+1}; else npc_reply_{replyIdx+1} */
  mood?: ReplyMood;
  at: number;
}

/** Scheduled NPC reaction (delivered by commentSystem at dueAt) */
export interface PendingReply {
  instanceId: string;
  nickIdx: number;
  replyIdx: number;
  mood?: ReplyMood;
  dueAt: number;
}

/** The next news, already decided but not yet published (leak refers to it) */
export interface PendingNews {
  instanceId: string;
  newsId: string;
  coinId: string;
  direction: NewsDirection;
  impact: number;
  duration: number;
  leakId: string;
}

export interface InfoState {
  /** newest first, max NEWS_CONFIG.feedLimit items */
  feed: FeedItem[];
  activeNews: ActiveCoinNews[];
  pendingNews: PendingNews | null;
  /** when the leak for the next news drops */
  nextLeakAt: number;
  /** when the next news publishes */
  nextPublishAt: number;
  /** player comments on feed news (trimmed with the feed, Sprint 3a) */
  playerComments: PlayerComment[];
  /** delivered NPC reactions to player comments */
  npcReplies: NpcReply[];
  /** scheduled NPC reactions (5~15s after the player posts) */
  pendingReplies: PendingReply[];
  /**
   * Shuffle-bag cursors for info-side text pools (news-comment memes per
   * direction, NPC reply pools per mood). Only { seed, cursor } is stored.
   */
  bags: BagStore;
}

export type WhalePhase = 'dormant' | 'accumulating' | 'pumping' | 'dumping';

export interface WhaleState {
  phase: WhalePhase;
  phaseEndsAt: number;
  /** price when the current accumulation started (dump target is below this) */
  accumStartPrice: number;
  /** ad hook: live phase label visible in UI until this time (3 min) */
  trackerRevealUntil: number;
  /** ad hook: tracker button locked until this time (90s cooldown) */
  trackerCooldownUntil: number;
  /** last time a pumping phase ended (rug pull trigger source, Sprint 2b) */
  pumpEndedAt: number;
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function createInitialInfoState(): InfoState {
  const now = Date.now();
  const publishAt = now + randRange(NEWS_CONFIG.firstNewsDelayMin, NEWS_CONFIG.firstNewsDelayMax)
    + randRange(NEWS_CONFIG.leakLeadMin, NEWS_CONFIG.leakLeadMax);
  const leakAt = publishAt - randRange(NEWS_CONFIG.leakLeadMin, NEWS_CONFIG.leakLeadMax);
  return {
    feed: [],
    activeNews: [],
    pendingNews: null,
    nextLeakAt: Math.max(now + 2000, leakAt),
    nextPublishAt: publishAt,
    playerComments: [],
    npcReplies: [],
    pendingReplies: [],
    bags: {},
  };
}

/** A fresh dormant whale for one coin (used at init and on (re)listing). */
export function freshWhale(startPrice: number, now: number): WhaleState {
  return {
    phase: 'dormant',
    phaseEndsAt: now + randRange(WHALE_CONFIG.dormantMin, WHALE_CONFIG.dormantMax),
    accumStartPrice: startPrice,
    trackerRevealUntil: 0,
    trackerCooldownUntil: 0,
    pumpEndedAt: 0,
  };
}

export function createInitialWhales(): Record<string, WhaleState> {
  const now = Date.now();
  const whales: Record<string, WhaleState> = {};
  for (const cfg of COINS) {
    if (cfg.tier !== 'meme') continue;
    whales[cfg.id] = freshWhale(cfg.startPrice, now);
  }
  return whales;
}

/** Migrate a loaded save: fill missing fields with defaults (backward compatible). */
export function migrateInfoState(saved: Partial<InfoState> | undefined): InfoState {
  const fresh = createInitialInfoState();
  if (!saved) return fresh;
  return {
    feed: Array.isArray(saved.feed) ? saved.feed : fresh.feed,
    activeNews: Array.isArray(saved.activeNews) ? saved.activeNews : fresh.activeNews,
    pendingNews: saved.pendingNews ?? null,
    nextLeakAt: saved.nextLeakAt ?? fresh.nextLeakAt,
    nextPublishAt: saved.nextPublishAt ?? fresh.nextPublishAt,
    playerComments: Array.isArray(saved.playerComments) ? saved.playerComments : [],
    npcReplies: Array.isArray(saved.npcReplies) ? saved.npcReplies : [],
    pendingReplies: Array.isArray(saved.pendingReplies) ? saved.pendingReplies : [],
    bags: migrateBags(saved.bags),
  };
}

/** Migrate whales: keep saved phase data for known meme coins, add missing ones. */
export function migrateWhales(
  saved: Record<string, Partial<WhaleState>> | undefined
): Record<string, WhaleState> {
  const fresh = createInitialWhales();
  if (!saved) return fresh;
  const result: Record<string, WhaleState> = {};
  for (const [coinId, freshWhale] of Object.entries(fresh)) {
    const s = saved[coinId];
    result[coinId] = s && typeof s.phase === 'string'
      ? {
          phase: s.phase,
          phaseEndsAt: s.phaseEndsAt ?? freshWhale.phaseEndsAt,
          accumStartPrice: s.accumStartPrice ?? freshWhale.accumStartPrice,
          trackerRevealUntil: s.trackerRevealUntil ?? 0,
          trackerCooldownUntil: s.trackerCooldownUntil ?? 0,
          pumpEndedAt: s.pumpEndedAt ?? 0,
        }
      : freshWhale;
  }
  return result;
}
